import { deriveContractStatus } from "app/utils/contractState";

// Single seam between the backend `/jobs/` contract and the shape the Jobs UI
// reads, so the components and status maps stay in FE terms.
//
// TODO(AS-921): delete this file once the two contracts converge.

const adaptInspection = (value) =>
  typeof value === "string" ? value.toLowerCase() : value;

// Backend InspectionPayment status → FE payment model. `paid` is a real Stripe
// charge → "paid_stripe". Refund/dispute states each have their own chip
// `disputed` is split further by `dispute_status` below.
const PAYMENT_STATUS = {
  no_payment: "no_payment",
  unpaid: "unpaid",
  paid: "paid_stripe",
  paid_manual: "paid_manual",
  refunded: "refunded",
  disputed: "disputed",
  dispute_lost: "dispute_lost",
};

// Evidence is already in and only the decision is outstanding; anything else
// keeps the louder "Disputed" chip.
const DISPUTE_UNDER_REVIEW = ["under_review", "warning_under_review"];

// Backend EmailStatus → FE email-step key.
const EMAIL_STATUS = {
  ready: "ready_to_send",
  failed: "failed",
  cancelled: "canceled",
  pending: "pending",
  sent: "sent",
};

const adaptPayment = (payment) => {
  if (!payment) return "no_payment";
  const status = PAYMENT_STATUS[payment.status] || "no_payment";
  if (
    status === "disputed" &&
    DISPUTE_UNDER_REVIEW.includes(payment.dispute_status)
  ) {
    return "disputed_review";
  }
  return status;
};

// The chip renders `{count} {status}` per the design spec, count shown even at
// 1. A payload without `count` falls back to the bare label.
const adaptEmail = (email) =>
  email && email.status
    ? { key: EMAIL_STATUS[email.status] || email.status, count: email.count }
    : null;

/**
 * Recombine the backend's split date and time into one naive datetime string,
 * which is what formatListDate/formatListTime expect.
 */
const combineDateTime = (date, time) =>
  date && time ? `${date}T${time}` : null;

/** Adapt one backend list row into the shape the Jobs UI reads. */
export const adaptJobRow = (row) => ({
  id: row.job_id,
  date: row.representative_date || null,
  start_time: combineDateTime(row.representative_date, row.representative_time),
  address: row.address || "",
  client_name: row.client_name || "",
  inspection_status: adaptInspection(row.inspection_status),
  contract_status: deriveContractStatus(row.contract),
  payment_status: adaptPayment(row.payment),
  email_aggregate: adaptEmail(row.email),
});

/**
 * Adapt the list envelope. `count` is documented as always equal to
 * `counts[tab]`, hence the fallback.
 *
 * The envelope's `truncated` flag is deliberately not surfaced: it needs >500
 * jobs on one tab to fire, and V1 has no search to offer as a next step.
 *
 * @param {Object} payload - { results, count, counts, truncated }
 * @param {string} tab - the tab the payload was fetched for
 */
export const adaptJobList = (payload, tab) => {
  const counts = payload.counts || {};
  return {
    results: (payload.results || []).map(adaptJobRow),
    count: payload.count ?? counts[tab] ?? 0,
    counts,
  };
};

// --- Detail (`GET /jobs/{id}/`) ---------------------------------------------
// The backend returns an 8-card aggregate (header/booking/setup/domains/
// inspection/report/email); the Job Detail page + its cards read the flat
// fixture shape. Flatten it here. `id` is threaded in from the request arg —
// the detail payload has no top-level id.

// header.address is the backend's one-line string; the parts still seed the
// Edit modal's fields. Until backend #240 deploys it holds line 1 alone, and
// showing that would drop the city, so the heading joins the parts instead.
const adaptDetailAddress = (address, formatted) => ({
  formatted_address:
    formatted && formatted !== address?.address_line1 ? formatted : "",
  ...(address && {
    line1: address.address_line1 || "",
    line2: address.address_line2 || "",
    city: address.city || "",
    state: address.state || "",
    zip: address.zip_code || "",
  }),
});

// Backend booking.report (AppointmentFormatter._format_report_data) → the flat
// property block the FE reads (note the field renames).
const adaptProperty = (report) => ({
  year_built: report?.year_build || "",
  square_feet: report?.floor_area || "",
  stories: report?.stories || "",
  house_type: report?.house_type || "",
  parcel_number: report?.apn || "",
  info: report?.info || "",
});

const adaptEmailList = (emails) =>
  (emails || []).map((email) => ({
    id: email.id,
    name: email.name || email.event_type,
    status: EMAIL_STATUS[email.status] || email.status,
    timestamp: email.sent_at || email.scheduled_send_at || null,
    description: email.error_message || null,
    content: null,
  }));

// method is inferred from the *raw* backend status before it is remapped.
const adaptPaymentMethod = (status) =>
  status === "paid_manual" ? "manual" : status === "paid" ? "stripe" : null;

export const adaptJobDetail = (payload, id) => {
  const booking = payload.booking || {};
  const report = booking.report || null;
  const appt = booking.appointment || {};
  const notes = booking.notes || {};
  const setup = payload.setup || {};
  const domains = (payload.domains && payload.domains.domains) || {};
  const contract = domains.contract || null;
  const payment = domains.payment || null;

  return {
    id,
    appointment_id: appt.id || null,
    date: appt.date || null,
    start_time: appt.start_time || null,
    end_time: appt.end_time || null,
    status: adaptInspection(payload.header && payload.header.status),
    address: adaptDetailAddress(
      report && report.address,
      payload.header && payload.header.address
    ),
    client: (report && report.client) || null,
    agent: (report && report.agent) || null,
    // Read by toAppointmentShape to seed the Edit modal's agent-N/A state.
    agent_applicable: report ? report.agent_applicable : false,
    property: adaptProperty(report),
    note: notes.appointment_note || "",
    job_setup: {
      // Sent straight back by PUT /calendar/jobs/{id}/. Hardcoding this to null
      // made the Setup modal's dropdown reset, so saving replaced the job's
      // section template with the default.
      template_id: setup.template_id || null,
      template_name: setup.template_name || null,
      quote: setup.quote != null ? setup.quote : null,
      note: setup.note || "",
      // A system-template send returns an id with no name.
      is_setup: Boolean(setup.template_name || setup.template_id),
    },
    contract: {
      // Revoke, Reset Status and Download all address the contract by id.
      // Null before the first send, when there is no Contract row yet.
      id: (contract && contract.id) || null,
      status: deriveContractStatus(contract),
      // Carried for card copy, dates and debugging only — nothing branches on
      // these. `reissue_available` in particular is absent from the jobs LIST
      // row, so branching on it would split the list chip from the detail card.
      raw_status: (contract && contract.status) || null,
      reissue_required: Boolean(contract && contract.reissue_required),
      reissue_available: Boolean(contract && contract.reissue_available),
      alert: (contract && contract.alert) || null,
      template_name: (contract && contract.template_name) || null,
      // "user" | "system" — disambiguates a null template_name (system send, or
      // a user template deleted after the send).
      template_source: (contract && contract.template_source) || null,
      sent_at: (contract && contract.sent_at) || null,
      signed_at: (contract && contract.signed_at) || null,
      // Gates Download, and dates the Signed card when signed_at is null (a
      // dropped Signed webhook, or the hourly reconciliation path).
      completed_at: (contract && contract.completed_at) || null,
      voided_at: (contract && contract.voided_at) || null,
      // The Revoked card shows the reason as its headline, so both the
      // structured code and the free-text note are carried through.
      void_reason: (contract && contract.void_reason) || null,
      void_reason_code: (contract && contract.void_reason_code) || null,
    },
    payment: {
      // The InspectionPayment row id. Cancel Request and Undo address the row
      // directly, so without this the card can render a button it can't call.
      // Null when the job has no payment row at all.
      id: (payment && payment.id) || null,
      status: adaptPayment(payment),
      amount:
        payment && payment.amount_cents != null
          ? payment.amount_cents / 100
          : null,
      method: adaptPaymentMethod(payment && payment.status),
      request_url: (payment && payment.payment_link_url) || null,
      // created_at = when the row was recorded (the paid card renders it);
      // paid_at is set only by a real Stripe charge, null for paid_manual.
      created_at: (payment && payment.created_at) || null,
      paid_at: (payment && payment.paid_at) || null,
      // Refund/dispute lifecycle. The dispute card renders the
      // evidence deadline as its "Respond by" line.
      refunded_at: (payment && payment.refunded_at) || null,
      dispute_status: (payment && payment.dispute_status) || null,
      dispute_evidence_due_by:
        (payment && payment.dispute_evidence_due_by) || null,
      dispute_closed_at: (payment && payment.dispute_closed_at) || null,
    },
    report_id: (report && report.id) || null,
    inspection: {
      item_count: (payload.inspection && payload.inspection.item_count) || 0,
    },
    report: {
      compiled: Boolean(payload.report && payload.report.compiled_at),
      compiled_at: (payload.report && payload.report.compiled_at) || null,
      thumbnail_url: (payload.report && payload.report.cover_photo_url) || null,
      compiled_report_id:
        (report && report.compiled_report && report.compiled_report.id) || null,
    },
    emails: adaptEmailList(payload.email),
  };
};
